import { ReactNode } from 'react';

export type BadgeProps = {
    label?: string;
    shape: 'rounded' | 'squared';
    size: 'small' | 'regular';
    color: 'red' | 'blue' | 'green' | 'yellow' | 'purple' | 'gray' | 'pink' | 'indigo';
    variant?: 'default' | 'outline' | 'filled' | 'dot';
    icon?: ReactNode;
};

const styles = {
    size: {
        small: 'px-2.5 py-0.5 text-xs',
        regular: 'px-3 py-0.5 text-sm',
        iconOnlySmall: 'text-xs p-1',
        iconOnlyRegular: 'text-sm p-1.5',
    },
    shape: { rounded: 'rounded-full', squared: 'rounded' },
    colors: {
        red: {
            default: 'text-red-700 bg-red-100 dark:text-red-100 dark:bg-red-500/30',
            filled: 'text-white bg-red-600',
            outline: 'border border-red-600 text-red-600 dark:border-red-400 dark:text-red-400 bg-transparent',
            dot: 'dark:text-gray-300 dark:fill-red-400 text-gray-600 fill-red-600 border border-gray-300',
        },
        blue: {
            default: 'text-blue-700 bg-blue-100 dark:text-blue-100 dark:bg-blue-500/30',
            filled: 'text-white bg-blue-600',
            outline: 'border border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400 bg-transparent',
            dot: 'dark:text-gray-300 dark:fill-blue-400 text-gray-600 fill-blue-600 border border-gray-300',
        },
        green: {
            default: 'text-green-700 bg-green-100 dark:text-green-100 dark:bg-green-500/30',
            filled: 'text-white bg-green-600',
            outline: 'border border-green-600 text-green-600 dark:border-green-400 dark:text-green-400 bg-transparent',
            dot: 'dark:text-gray-300 dark:fill-green-400 text-gray-600 fill-green-600 border border-gray-300',
        },
        yellow: {
            default: 'text-yellow-700 bg-yellow-100 dark:text-yellow-100 dark:bg-yellow-500/30',
            filled: 'text-white bg-yellow-600',
            outline: 'border border-yellow-600 text-yellow-600 dark:border-yellow-400 dark:text-yellow-400 bg-transparent',
            dot: 'dark:text-gray-300 dark:fill-yellow-400 text-gray-600 fill-yellow-600 border border-gray-300',
        },
        purple: {
            default: 'text-purple-700 bg-purple-100 dark:text-purple-100 dark:bg-purple-500/30',
            filled: 'text-white bg-purple-600',
            outline: 'border border-purple-600 text-purple-600 dark:border-purple-400 dark:text-purple-400 bg-transparent',
            dot: 'dark:text-gray-300 dark:fill-purple-400 text-gray-600 fill-purple-600 border border-gray-300',
        },
        gray: {
            default: 'text-gray-700 bg-gray-100 dark:text-gray-100 dark:bg-gray-500/30',
            filled: 'text-white bg-gray-600',
            outline: 'border border-gray-600 text-gray-600 dark:border-gray-400 dark:text-gray-400 bg-transparent',
            dot: 'dark:text-gray-300 dark:fill-gray-400 text-gray-600 fill-gray-600 border border-gray-300',
        },
        pink: {
            default: 'text-pink-700 bg-pink-100 dark:text-pink-100 dark:bg-pink-500/30',
            filled: 'text-white bg-pink-600',
            outline: 'border border-pink-600 text-pink-600 dark:border-pink-400 dark:text-pink-400 bg-transparent',
            dot: 'dark:text-gray-300 dark:fill-pink-400 text-gray-600 fill-pink-600 border border-gray-300',
        },
        indigo: {
            default: 'text-indigo-700 bg-indigo-100 dark:text-indigo-100 dark:bg-indigo-500/30',
            filled: 'text-white bg-indigo-600',
            outline: 'border border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400 bg-transparent',
            dot: 'dark:text-gray-300 dark:fill-indigo-400 text-gray-600 fill-indigo-600 border border-gray-300',
        },
    },
};

function Badgev2({ label, shape, size, color, icon, variant = 'default' }: BadgeProps): JSX.Element {
    // If there is only an icon and no label, we need to apply sizing differently
    let sizeStyle: 'small' | 'regular' | 'iconOnlySmall' | 'iconOnlyRegular' = 'small';
    if (icon && !label) {
        sizeStyle = size === 'small' ? 'iconOnlySmall' : 'iconOnlyRegular';
    } else {
        sizeStyle = size;
    }

    return (
        <div
            className={`font-medium inline-flex items-center ${styles.size[sizeStyle]} ${styles.shape[shape]} ${styles.colors[color][variant]}`}
        >
            {variant === 'dot' && (
                <svg className="mr-1.5 h-2 w-2" viewBox="0 0 8 8">
                    <circle cx={4} cy={4} r={3} />
                </svg>
            )}
            {icon !== undefined && (
                <span className={`${label ? 'mr-1.5' : ''} ${size === 'small' ? 'h-3.5 w-3.5' : 'h-5 w-5'}`}>{icon}</span>
            )}
            {label}
        </div>
    );
}

export default Badgev2;
